import _en from './translations/en.json.mjs';
import React, { useRef, useEffect, useCallback } from 'react';
import { hsbToRgb, ActionList, Popover, EventListener } from '@shopify/polaris';
import { useI18n } from '@shopify/react-i18n';
import { classNames } from '@shopify/css-utilities';
import { useMedia } from '@shopify/react-hooks';
import { useDebouncedCallback } from 'use-debounce';
import TextColor from '../TextColor/TextColor.mjs';
import { TypographicElement, LinkSelectionType, RteModal, ToolbarElement, RtePopover, TypographyCommand, RteCommand } from '../../types/index.mjs';
import styles from './Toolbar.scss.mjs';
import { useOverflowMeasurements } from './hooks/use-overflow-measurements.mjs';
import TooltipWrapper from './components/TooltipWrapper/TooltipWrapper.mjs';
import { Button } from './components/Button/Button.mjs';
import rteHtml from './icons/rte-html.svg.mjs';
import rteLink from './icons/rte-link.svg.mjs';
import rteImage from './icons/rte-image.svg.mjs';
import { FormattingList } from './components/FormattingList/FormattingList.mjs';
import rteBold from './icons/rte-bold.svg.mjs';
import rteItalic from './icons/rte-italic.svg.mjs';
import rteUnderline from './icons/rte-underline.svg.mjs';
import rteColor from './icons/rte-color.svg.mjs';
import rteAlignLeft from './icons/rte-align-left.svg.mjs';
import rteAlignCenter from './icons/rte-align-center.svg.mjs';
import rteAlignRight from './icons/rte-align-right.svg.mjs';
import rteVideo from './icons/rte-video.svg.mjs';
import rteTable from './icons/rte-table.svg.mjs';
import rteUnorderedList from './icons/rte-list.svg.mjs';
import rteOrderedList from './icons/rte-list-ordered.svg.mjs';
import rteOutdent from './icons/rte-outdent.svg.mjs';
import rteIndent from './icons/rte-indent.svg.mjs';
import rteClear from './icons/rte-clear.svg.mjs';
import rteOverflow from './icons/rte-overflow.svg.mjs';

const Toolbar = /*#__PURE__*/React.memo(function Toolbar({
  id,
  showHTML,
  tableSelected,
  imageSelected,
  selectedBold,
  selectedItalic,
  selectedUnderline,
  selectedOrderedList,
  selectedUnorderedList,
  selectedTextColor,
  selectedBackgroundColor,
  selectedTypographicElement,
  linkSelectionType,
  inclusions,
  onToggleHTML,
  onColorChange,
  onNodeSelect,
  openModal,
  onOpenPopover,
  onClosePopover,
  executeCommand,
  popoverVisible,
  overflowVisible,
  onToggleOverflow,
  readOnly = false
}) {
  var _ref, _formattingOptions$fi;
  const [i18n] = useI18n({
    id: 'Toolbar_11c99c4',
    fallback: _en,
    translations(locale) {
      if (["cs", "da", "de", "es", "fi", "fr", "it", "ja", "ko", "nb", "nl", "pl", "pt-BR", "pt-PT", "sv", "th", "tr", "vi", "zh-CN", "zh-TW"].indexOf(locale) < 0) {
        return;
      }
      return import( /* webpackChunkName: "Toolbar_11c99c4-i18n", webpackMode: "lazy-once" */`./translations/${locale}.json`).then(dict => dict && dict.default);
    }
  });
  const controlsContainerRef = useRef(null);
  const controlsMeasurerRef = useRef(null);
  const overflowContainerRef = useRef(null);
  const {
    childrenThatFit,
    remeasureElements: handleResize
  } = useOverflowMeasurements({
    containerNode: controlsContainerRef,
    measurerNode: controlsMeasurerRef,
    overflowOffsetWidth: 74
  });
  const isLargeScreen = useMedia('(min-width: 480px)');
  useEffect(() => {
    handleResize();
  }, [handleResize]);
  const tableOptionsDisabled = !tableSelected;
  const formattingOptions = [{
    key: TypographicElement.Paragraph,
    label: i18n.translate('Toolbar.styles.paragraph')
  }, {
    key: TypographicElement.Heading1,
    label: i18n.translate('Toolbar.styles.heading', {
      headingLevel: 1
    })
  }, {
    key: TypographicElement.Heading2,
    label: i18n.translate('Toolbar.styles.heading', {
      headingLevel: 2
    })
  }, {
    key: TypographicElement.Heading3,
    label: i18n.translate('Toolbar.styles.heading', {
      headingLevel: 3
    })
  }, {
    key: TypographicElement.Heading4,
    label: i18n.translate('Toolbar.styles.heading', {
      headingLevel: 4
    })
  }, {
    key: TypographicElement.Heading5,
    label: i18n.translate('Toolbar.styles.heading', {
      headingLevel: 5
    })
  }, {
    key: TypographicElement.Heading6,
    label: i18n.translate('Toolbar.styles.heading', {
      headingLevel: 6
    })
  }, {
    key: TypographicElement.Blockquote,
    label: i18n.translate('Toolbar.styles.blockquote')
  }];
  const formattingLabel = (_ref = selectedTypographicElement && ((_formattingOptions$fi = formattingOptions.find(({
    key
  }) => key === selectedTypographicElement)) == null ? void 0 : _formattingOptions$fi.label)) != null ? _ref : i18n.translate('Toolbar.styles.paragraph');
  const updateRteColor = useDebouncedCallback((target, color) => {
    const {
      red,
      green,
      blue
    } = hsbToRgb(color);
    const rgbStyle = `rgb(${red}, ${green}, ${blue})`;
    getCommandExecutor(target, rgbStyle, false, true)();
  }, 150, {
    leading: true
  });
  const handleColorChange = target => color => {
    updateRteColor(target, color);
    onColorChange(target, color);
  };
  const handleListTypography = command => {
    return command === TypographyCommand.UnorderedList ? () => {
      if (selectedOrderedList) {
        handleTypography(TypographyCommand.OrderedList)();
      }
      handleTypography(TypographyCommand.UnorderedList)();
    } : () => {
      if (selectedUnorderedList) {
        handleTypography(TypographyCommand.UnorderedList)();
      }
      handleTypography(TypographyCommand.OrderedList)();
    };
  };
  const handleTypography = command => () => {
    getCommandExecutor(command)();
    onNodeSelect(command);
  };
  const handleInsertTable = () => {
    onClosePopover();
    getCommandExecutor('mceInsertContent', '<table width="100%"><tbody><tr><td>&nbsp;</td></tr></tbody></table>')();
  };
  const getCommandExecutor = (command, value, closePopover = true, skipFocus) => {
    return () => {
      executeCommand(command, value, skipFocus ? {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        skip_focus: true
      } : undefined);
      if (closePopover) {
        onClosePopover();
      }
    };
  };
  const inclusionMap = new Map();
  inclusions == null ? void 0 : inclusions.forEach(inclusion => {
    inclusionMap.set(inclusion, true);
  });
  const htmlButtonText = i18n.translate(showHTML ? 'Toolbar.hideHTML' : 'Toolbar.showHTML');
  const htmlButton = /*#__PURE__*/React.createElement(TooltipWrapper, {
    content: htmlButtonText
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.ButtonContainer
  }, /*#__PURE__*/React.createElement(Button, {
    icon: rteHtml,
    accessibilityLabel: htmlButtonText,
    onClick: onToggleHTML,
    pressed: showHTML,
    bordered: true
  })));
  const editorButtonsDisabled = showHTML || readOnly;
  let linkSelectionItem;
  switch (linkSelectionType) {
    case LinkSelectionType.Insert:
      {
        linkSelectionItem = {
          label: i18n.translate('RichTextEditor.insertLink'),
          icon: rteLink,
          onClick: openModal(RteModal.UpdateLink),
          pressed: false,
          disabled: editorButtonsDisabled
        };
        break;
      }
    case LinkSelectionType.Edit:
      {
        linkSelectionItem = {
          label: i18n.translate('RichTextEditor.editLink'),
          icon: rteLink,
          onClick: openModal(RteModal.UpdateLink),
          pressed: false,
          disabled: editorButtonsDisabled
        };
        break;
      }
    case LinkSelectionType.Empty:
      {
        linkSelectionItem = {
          label: i18n.translate('Toolbar.insertLinkDisabled'),
          icon: rteLink,
          onClick: openModal(RteModal.UpdateLink),
          pressed: false,
          disabled: true
        };
        break;
      }
  }
  const imageItem = imageSelected ? {
    label: i18n.translate('RichTextEditor.editImage'),
    icon: rteImage,
    onClick: openModal(RteModal.EditImage),
    pressed: false,
    disabled: editorButtonsDisabled
  } : {
    label: i18n.translate('RichTextEditor.insertImage'),
    icon: rteImage,
    onClick: openModal(RteModal.InsertImage),
    pressed: false,
    disabled: editorButtonsDisabled
  };
  const handleFormattingSelect = selectedFormatting => {
    getCommandExecutor('formatBlock', selectedFormatting)();
  };
  function filterToolbar(items) {
    return items.filter(i => i !== false);
  }
  const controlsSections = filterToolbar([inclusionMap.has(ToolbarElement.TextFormat) && {
    key: 'formatting',
    items: [{
      label: i18n.translate('Toolbar.formatting'),
      accessibilityLabel: i18n.translate('Toolbar.selectedFormatting', {
        format: formattingLabel
      }),
      content: formattingLabel,
      pressed: popoverVisible === RtePopover.Formatting,
      disabled: editorButtonsDisabled,
      popover: {
        key: RtePopover.Formatting,
        active: popoverVisible === RtePopover.Formatting,
        fullHeight: true,
        children: /*#__PURE__*/React.createElement(FormattingList, {
          selected: selectedTypographicElement,
          options: formattingOptions,
          onSelect: handleFormattingSelect
        })
      }
    }]
  }, {
    key: 'text-modifiers',
    items: filterToolbar([inclusionMap.has(ToolbarElement.TextModifierBold) && {
      label: i18n.translate('Toolbar.bold'),
      icon: rteBold,
      onClick: handleTypography(TypographyCommand.Bold),
      pressed: selectedBold,
      disabled: editorButtonsDisabled
    }, inclusionMap.has(ToolbarElement.TextModifierItalic) && {
      label: i18n.translate('Toolbar.italic'),
      icon: rteItalic,
      onClick: handleTypography(TypographyCommand.Italic),
      pressed: selectedItalic,
      disabled: editorButtonsDisabled
    }, inclusionMap.has(ToolbarElement.TextModifierUnderline) && {
      label: i18n.translate('Toolbar.underline'),
      icon: rteUnderline,
      onClick: handleTypography(TypographyCommand.Underline),
      pressed: selectedUnderline,
      disabled: editorButtonsDisabled
    }, inclusionMap.has(ToolbarElement.TextModifierColor) && {
      label: i18n.translate('Toolbar.color'),
      icon: rteColor,
      pressed: popoverVisible === RtePopover.Color,
      disabled: editorButtonsDisabled,
      popover: {
        key: RtePopover.Color,
        active: popoverVisible === RtePopover.Color,
        fullHeight: true,
        children: /*#__PURE__*/React.createElement(TextColor, {
          textColor: selectedTextColor,
          backgroundColor: selectedBackgroundColor,
          onTextChange: handleColorChange(RteCommand.TextColor),
          onBackgroundChange: handleColorChange(RteCommand.BackgroundColor),
          onColorPresetSelection: onClosePopover
        })
      }
    }])
  }, inclusionMap.has(ToolbarElement.TextAlignment) && {
    key: 'text-alignment',
    items: [{
      label: i18n.translate('Toolbar.alignment'),
      icon: rteAlignLeft,
      pressed: popoverVisible === RtePopover.Alignment,
      disabled: editorButtonsDisabled,
      popover: {
        key: RtePopover.Alignment,
        active: popoverVisible === RtePopover.Alignment,
        children: /*#__PURE__*/React.createElement("div", {
          className: styles.OverflowContainer
        }, /*#__PURE__*/React.createElement(TooltipWrapper, {
          content: i18n.translate('RichTextEditor.alignments.left')
        }, /*#__PURE__*/React.createElement(Button, {
          icon: rteAlignLeft,
          accessibilityLabel: i18n.translate('RichTextEditor.alignments.left'),
          onClick: getCommandExecutor('justifyleft')
        })), /*#__PURE__*/React.createElement(TooltipWrapper, {
          content: i18n.translate('RichTextEditor.alignments.center')
        }, /*#__PURE__*/React.createElement(Button, {
          icon: rteAlignCenter,
          accessibilityLabel: i18n.translate('RichTextEditor.alignments.center'),
          onClick: getCommandExecutor('justifycenter')
        })), /*#__PURE__*/React.createElement(TooltipWrapper, {
          content: i18n.translate('RichTextEditor.alignments.right')
        }, /*#__PURE__*/React.createElement(Button, {
          icon: rteAlignRight,
          accessibilityLabel: i18n.translate('RichTextEditor.alignments.right'),
          onClick: getCommandExecutor('justifyright')
        })))
      }
    }]
  }, {
    key: 'insert-controls',
    items: filterToolbar([inclusionMap.has(ToolbarElement.InsertLink) && linkSelectionItem, inclusionMap.has(ToolbarElement.InsertImage) && imageItem, inclusionMap.has(ToolbarElement.InsertVideo) && {
      label: i18n.translate('RichTextEditor.insertVideo'),
      icon: rteVideo,
      onClick: openModal(RteModal.InsertVideo),
      pressed: false,
      disabled: editorButtonsDisabled
    }, inclusionMap.has(ToolbarElement.InsertTable) && {
      label: i18n.translate('Toolbar.insertTable'),
      icon: rteTable,
      pressed: popoverVisible === RtePopover.Table,
      disabled: editorButtonsDisabled,
      popover: {
        key: RtePopover.Table,
        active: popoverVisible === RtePopover.Table,
        fullHeight: true,
        children: /*#__PURE__*/React.createElement(ActionList, {
          sections: [{
            items: [{
              content: i18n.translate('Toolbar.insertTable'),
              onAction: handleInsertTable
            }]
          }, {
            items: [{
              content: i18n.translate('Toolbar.tables.insertRowAbove'),
              disabled: tableOptionsDisabled,
              onAction: getCommandExecutor('mceTableInsertRowBefore')
            }, {
              content: i18n.translate('Toolbar.tables.insertRowBelow'),
              disabled: tableOptionsDisabled,
              onAction: getCommandExecutor('mceTableInsertRowAfter')
            }, {
              content: i18n.translate('Toolbar.tables.insertColBefore'),
              disabled: tableOptionsDisabled,
              onAction: getCommandExecutor('mceTableInsertColBefore')
            }, {
              content: i18n.translate('Toolbar.tables.insertColAfter'),
              disabled: tableOptionsDisabled,
              onAction: getCommandExecutor('mceTableInsertColAfter')
            }]
          }, {
            items: [{
              content: i18n.translate('Toolbar.tables.deleteRow'),
              disabled: tableOptionsDisabled,
              onAction: getCommandExecutor('mceTableDeleteRow')
            }, {
              content: i18n.translate('Toolbar.tables.deleteCol'),
              disabled: tableOptionsDisabled,
              onAction: getCommandExecutor('mceTableDeleteCol')
            }, {
              content: i18n.translate('Toolbar.tables.deleteTable'),
              disabled: tableOptionsDisabled,
              onAction: getCommandExecutor('mceTableDelete')
            }]
          }]
        })
      }
    }])
  }, {
    key: 'list-formatting',
    items: filterToolbar([inclusionMap.has(ToolbarElement.UnorderedList) && {
      label: i18n.translate('Toolbar.unorderedList'),
      icon: rteUnorderedList,
      onClick: handleListTypography(TypographyCommand.UnorderedList),
      pressed: selectedUnorderedList,
      disabled: editorButtonsDisabled
    }, inclusionMap.has(ToolbarElement.OrderedList) && {
      label: i18n.translate('Toolbar.orderedList'),
      icon: rteOrderedList,
      onClick: handleListTypography(TypographyCommand.OrderedList),
      pressed: selectedOrderedList,
      disabled: editorButtonsDisabled
    }, inclusionMap.has(ToolbarElement.Outdent) && {
      label: i18n.translate('Toolbar.outdent'),
      icon: rteOutdent,
      onClick: getCommandExecutor('outdent'),
      pressed: false,
      disabled: editorButtonsDisabled
    }, inclusionMap.has(ToolbarElement.Indent) && {
      label: i18n.translate('Toolbar.indent'),
      icon: rteIndent,
      onClick: getCommandExecutor('indent'),
      pressed: false,
      disabled: editorButtonsDisabled
    }])
  }, inclusionMap.has(ToolbarElement.ClearFormatting) && {
    key: 'clear-formatting',
    items: [{
      label: i18n.translate('Toolbar.clearFormatting'),
      icon: rteClear,
      onClick: getCommandExecutor('removeformat'),
      pressed: false,
      disabled: editorButtonsDisabled
    }]
  }]);
  const controlSectionsThatFit = controlsSections.slice(0, childrenThatFit);
  const overflowControlSections = childrenThatFit < controlsSections.length && controlsSections.slice(childrenThatFit, controlsSections.length);
  const overflowControlsActivator = /*#__PURE__*/React.createElement(Button, {
    id: `richtext_editor_toolbar_overflow_controls_activator-${id}`,
    icon: rteOverflow,
    accessibilityLabel: i18n.translate('Toolbar.additionalControls'),
    onClick: onToggleOverflow,
    pressed: overflowVisible,
    disabled: editorButtonsDisabled && isLargeScreen
  });
  const handleOverflowPopoverClose = () => {
    if (popoverVisible === undefined && overflowVisible) {
      onToggleOverflow();
    }
  };
  const smallScreenHTMLButton = !isLargeScreen && /*#__PURE__*/React.createElement("div", {
    className: styles.Section
  }, /*#__PURE__*/React.createElement(TooltipWrapper, {
    content: htmlButtonText
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.ButtonContainer
  }, /*#__PURE__*/React.createElement(Button, {
    icon: rteHtml,
    accessibilityLabel: htmlButtonText,
    onClick: onToggleHTML,
    pressed: showHTML
  }))));
  const overflowControlsMarkup = overflowControlSections && /*#__PURE__*/React.createElement("div", {
    className: styles.Overflow,
    ref: overflowContainerRef
  }, /*#__PURE__*/React.createElement(Popover, {
    active: overflowVisible,
    activator: overflowControlsActivator,
    onClose: handleOverflowPopoverClose
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.OverflowContainer
  }, overflowControlSections.map(section => /*#__PURE__*/React.createElement(ControlsSection, {
    id: id,
    key: section.key,
    section: section,
    overflow: true,
    popoverVisible: popoverVisible,
    onOpenPopover: onOpenPopover,
    onClosePopover: onClosePopover
  })), smallScreenHTMLButton)));
  const measurerControlsSections = controlsSections.map(section => {
    const newSection = {
      ...section
    };
    newSection.items = section.items.map(({
      popover,
      ...itemAttributes
    }) => {
      return {
        ...itemAttributes,
        ...(popover && {
          popover: {
            ...popover,
            active: false,
            children: null
          }
        })
      };
    });
    return newSection;
  });
  const toolbarClassNames = classNames(styles.Toolbar, editorButtonsDisabled && styles.disabled);
  const largeScreenAdditionalActions = isLargeScreen && /*#__PURE__*/React.createElement("div", {
    className: styles.AdditionalActionsContainer
  }, htmlButton);
  return /*#__PURE__*/React.createElement("div", {
    className: toolbarClassNames,
    id: `richtexteditor_toolbar-${id}`
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.Layout
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.ControlsContainer,
    ref: controlsContainerRef
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.Controls
  }, controlSectionsThatFit.map(section => /*#__PURE__*/React.createElement(ControlsSection, {
    key: section.key,
    section: section,
    popoverVisible: popoverVisible,
    onOpenPopover: onOpenPopover,
    onClosePopover: onClosePopover
  }))), overflowControlsMarkup), largeScreenAdditionalActions), /*#__PURE__*/React.createElement(EventListener, {
    event: "resize",
    handler: handleResize
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.Measurer,
    ref: controlsMeasurerRef
  }, measurerControlsSections.map(section => /*#__PURE__*/React.createElement(ControlsSection, {
    key: section.key,
    section: section,
    popoverVisible: popoverVisible,
    onOpenPopover: onOpenPopover,
    onClosePopover: onClosePopover
  }))));
});
function getNestedPopoverZIndexOverride(id) {
  const overflowControlsElement = document.getElementById(`richtext_editor_toolbar_overflow_controls_activator-${id}`);
  if (overflowControlsElement) {
    const layerNode = overflowControlsElement.closest('[data-polaris-layer]');
    return layerNode ? parseInt(window.getComputedStyle(layerNode).zIndex || '0', 10) + 2 : undefined;
  }
  return undefined;
}
function ControlsSection({
  id,
  section: {
    items,
    key
  },
  overflow,
  popoverVisible,
  onOpenPopover,
  onClosePopover
}) {
  const getPopoverCallback = useCallback((popover, overflow) => () => {
    if (popover === popoverVisible) {
      onClosePopover();
    } else {
      onOpenPopover(popover, overflow);
    }
  }, [onClosePopover, onOpenPopover, popoverVisible]);
  const nestedPopoverZIndex = id ? getNestedPopoverZIndexOverride(id) : undefined;
  return /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.Section, key === 'insert-controls' && styles.SectionBreak),
    key: key
  }, items == null ? void 0 : items.map(({
    label,
    accessibilityLabel,
    content,
    onClick,
    pressed,
    disabled,
    icon,
    popover
  }) => {
    if (!popover) {
      return /*#__PURE__*/React.createElement(TooltipWrapper, {
        content: label,
        key: label,
        disabled: disabled
      }, /*#__PURE__*/React.createElement("div", {
        className: styles.ButtonContainer
      }, /*#__PURE__*/React.createElement(Button, {
        accessibilityLabel: label,
        onClick: onClick,
        pressed: pressed,
        disabled: disabled,
        icon: icon
      }, content)));
    }
    const activatorMarkup = /*#__PURE__*/React.createElement(TooltipWrapper, {
      content: label,
      disabled: disabled
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.ButtonContainer
    }, /*#__PURE__*/React.createElement(Button, {
      icon: icon,
      accessibilityLabel: accessibilityLabel ? accessibilityLabel : label,
      onClick: getPopoverCallback(popover.key, overflow),
      pressed: pressed,
      disabled: disabled,
      disclosure: true
    }, content)));
    return /*#__PURE__*/React.createElement(Popover, {
      key: label,
      active: popover.active,
      activator: activatorMarkup,
      onClose: onClosePopover,
      preferredPosition: "below",
      fullHeight: popover.fullHeight,
      zIndexOverride: nestedPopoverZIndex
    }, popover.children);
  }));
}

export { Toolbar };
